import { FC, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  Crop,
  makeAspectCrop,
  PixelCrop
} from 'react-image-crop';

import SegmentedControl, {
  SegmentedControlOption
} from '@components/segmented-control/SegmentedControl';
import {
  getLogoCropAspectRatio,
  logoCropAspectRatios,
  toLogoRatio
} from '@constants/file';
import ModalV2 from '@layouts/modal/Modal.v2';
import { LogoRatio } from '@types';

import 'react-image-crop/dist/ReactCrop.css';

const ratioOptions: SegmentedControlOption[] = logoCropAspectRatios.map(
  ({ key, value }) => ({ id: key, label: value })
);

interface ImageCropModalProps {
  fileName: string;
  previewUrl: string;
  initialRatio: LogoRatio;
  onClose: () => void;
  onConfirm: (croppedFile: File, ratio: LogoRatio) => void;
}

const ImageCropModal: FC<ImageCropModalProps> = ({
  onClose,
  onConfirm,
  previewUrl,
  fileName,
  initialRatio
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 50,
    height: 28.125
  });

  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [aspectRatioKey, setAspectRatioKey] = useState<LogoRatio>(initialRatio);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const aspectRatio = getLogoCropAspectRatio(aspectRatioKey);

  const applyAspectCrop = (img: HTMLImageElement, ratio: number) => {
    const percentCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 50
        },
        ratio,
        img.naturalWidth,
        img.naturalHeight
      ),
      img.naturalWidth,
      img.naturalHeight
    );
    const pixelCrop = convertToPixelCrop(percentCrop, img.width, img.height);

    setCrop(percentCrop);
    setCompletedCrop(pixelCrop);
  };

  const handleImageLoad = (img: HTMLImageElement) => {
    imageRef.current = img;
    applyAspectCrop(img, aspectRatio);
  };

  const handleAspectRatioChange = ({ id }: SegmentedControlOption) => {
    setAspectRatioKey(toLogoRatio(id));
    if (imageRef.current) {
      applyAspectCrop(imageRef.current, getLogoCropAspectRatio(id));
    }
  };

  const handleCompleteCrop = (currentCrop: PixelCrop) => {
    setCompletedCrop(currentCrop);
  };

  const createCroppedImage = async (
    cropRegion: PixelCrop
  ): Promise<File | null> => {
    if (!cropRegion || !imageRef.current || !canvasRef.current) {
      return null;
    }

    const canvas = canvasRef.current;
    const image = imageRef.current;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const pixelRatio = window.devicePixelRatio;

    canvas.width = cropRegion.width * pixelRatio * scaleX;
    canvas.height = cropRegion.height * pixelRatio * scaleY;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      cropRegion.x * scaleX,
      cropRegion.y * scaleY,
      cropRegion.width * scaleX,
      cropRegion.height * scaleY,
      0,
      0,
      canvas.width / pixelRatio,
      canvas.height / pixelRatio
    );

    return new Promise<File | null>((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return resolve(null);
        return resolve(new File([blob], fileName, { type: blob.type }));
      }, 'image/png');
    });
  };

  const handleConfirm = async () => {
    if (completedCrop) {
      const croppedFile = await createCroppedImage(completedCrop);
      if (croppedFile) {
        onConfirm(croppedFile, aspectRatioKey);
      }
    }
  };

  return (
    <ModalV2
      title="Crop and resize image"
      onClose={onClose}
      primaryButtonLabel="Confirm"
      primaryButtonClick={handleConfirm}
      primaryButtonDisabled={!completedCrop}
      secondaryButtonLabel="Cancel"
      secondaryButtonClick={onClose}
      bodyClassName="flex flex-col gap-4"
    >
      {previewUrl && (
        <div className="flex min-h-47 items-center justify-center overflow-hidden rounded-lg bg-cofeeNeutral10">
          <ReactCrop
            crop={crop}
            aspect={aspectRatio}
            onComplete={handleCompleteCrop}
            onChange={(newCrop) => setCrop(newCrop)}
          >
            <img
              src={previewUrl}
              className="!max-h-[300px] max-w-full"
              alt="Preview"
              onLoad={(e) => handleImageLoad(e.currentTarget)}
            />
          </ReactCrop>
        </div>
      )}

      <SegmentedControl
        options={ratioOptions}
        selectedId={aspectRatioKey}
        onChange={handleAspectRatioChange}
        className="flex w-full"
        segmentClassName="flex-1 py-2"
      />

      <canvas ref={canvasRef} className="hidden" />
    </ModalV2>
  );
};

export default ImageCropModal;
